import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IManagableObject } from 'app/system/admin-panel/IManagableObject';
import { map, Observable } from 'rxjs';
import {LocalizationService} from "../system/mobile-system/services/localization.service";
import {UsersService} from "./users.service";

@Injectable({
  providedIn: 'root'
})

export class CompanyModel implements ICompanyUpdateModel {
  TimeZone: string = "UTC";
  IsChecked: boolean = false;
  Id: number = 0;
  ParentCompanyId: number = null;
  Name: string;
  Phone: string = "";
  Email: string = "";
  PostAddress: string = "";
  ContactName: string = "";
  CompanyStatus: CompanyStatus = CompanyStatus.Active;
  CreatedAt: Date;

  init(data?: any) {
    if (data) {
      this.Id = data["id"];
      this.ParentCompanyId = data["parentCompanyId"];
      this.Name = data["name"];
      this.Phone = data["phone"];
      this.Email = data["email"];
      this.PostAddress = data["postAddress"];
      this.ContactName = data["contactName"];
      this.CompanyStatus = data["companyStatus"];
      this.TimeZone = data["timeZone"];
      this.CreatedAt = new Date(data["createdAt"]);
    }
  }

  static fromJS(data: any): CompanyModel {
    data = typeof data === 'object' ? data : {};
    let result = new CompanyModel();
    result.init(data);
    return result;
  }
}

export interface ICompanyCreateModel {
  TimeZone: string;
  ParentCompanyId: number;
  Name: string;
  Phone: string;
  Email: string;
  PostAddress: string;
  ContactName: string;
  CompanyStatus: CompanyStatus;
  CreatedAt: Date;
}

export enum CompanyStatus {
  Active = 1,
  Blocked = 0
}

export interface ICompanyUpdateModel extends ICompanyCreateModel {
  Id: number;
}
export class CompanyStatisticsViewModel implements IManagableObject {
  Id: number;
  Name: string;
  Status: CompanyStatus;
  ParentCompanyId: number;
  ParentCompanyName: number;
  ObjectCount: number;
  ObjectWithSubCompaniesCount: number;
  GeoZoneCount: number;
  IsChecked: boolean = false;
  init(data?: any) {
    if (data) {
      this.Id = data["companyId"];
      this.Name = data["name"];
      this.ParentCompanyId = data["parentCompanyId"];
      this.Status = data["companyStatus"];
      this.ObjectCount = data["objectCount"];
      this.ObjectWithSubCompaniesCount = data["objectWithSubCompaniesCount"];
      this.GeoZoneCount = data["geoZoneCount"];
      this.ParentCompanyName = data["parentCompanyName"];
      this.IsChecked = false;
    }
  }
  static fromJS(data: any): CompanyStatisticsViewModel {
    data = typeof data === 'object' ? data : {};
    let result = new CompanyStatisticsViewModel();
    result.init(data);
    return result;
  }
}
export interface ITelegramSettings {
  token: string;
  enabled: boolean;
}

export interface IEmailSettings {
  smtpHost: string;
  smtpPort: number;
  ssl: boolean;
  user: string;
  from: string;
  password: string;
  text: string;
  enabled: boolean;
}

export interface IExternalAPIViewModel {
  telegramSettings: ITelegramSettings;
  emailSettings: IEmailSettings;
  companyId: number;
}
@Injectable()
export class CompaniesService {

  constructor(
    private client: HttpClient,
    private localizationService: LocalizationService,
    private usersService: UsersService,
  ) {}

  public getCompanies(): Observable<CompanyModel[]> {
    return this.client.get<CompanyModel[]>("/ctrl/Companies").pipe(map(values => {
      const result: CompanyModel[] = [];
      if (Array.isArray(values)) {
        for (const item of values) {
          const company = CompanyModel.fromJS(item);
          if (company?.CreatedAt) {
            company.CreatedAt = this.localizationService.toUserTimeZoneDate(company.CreatedAt, this.usersService.getCurrentUser());
          }
          result.push(company);
        }
      }
      return result;
    }));
  }

  public getCompany(id: number) {
    return this.client.get<CompanyModel>(`/ctrl/Companies/${id}`).pipe(
      map(item => {
        const company = CompanyModel.fromJS(item);
        if (company?.CreatedAt) {
          company.CreatedAt = this.localizationService.toUserTimeZoneDate(company.CreatedAt, this.usersService.getCurrentUser());
        }
        return company;
      })
    );
  }

  public create(model: ICompanyCreateModel) {
    return this.client.post<CompanyModel>("/ctrl/Companies", model).pipe(
      map(item => {
        const company = CompanyModel.fromJS(item);
        if (company?.CreatedAt) {
          company.CreatedAt = this.localizationService.toUserTimeZoneDate(company.CreatedAt, this.usersService.getCurrentUser());
        }
        return company;
      })
    );
  }

  public getCompaniesStatistics(): Observable<CompanyStatisticsViewModel[]> {
    return this.client.get<CompanyStatisticsViewModel[]>("/ctrl/Companies/get-statistics").pipe(
      map(values => {
        const result: CompanyStatisticsViewModel[] = [];
        if (Array.isArray(values)) {
          for (const item of values) {
            result.push(CompanyStatisticsViewModel.fromJS(item));
          }
        }
        return result;
      })
    );
  }

  public getExternalAPIsByCompanyId(id: number) {
    return this.client.get<IExternalAPIViewModel>(`ctrl/Companies/get-settings/${id}`);
  }

  public saveSettings(settings: IExternalAPIViewModel) {
    return this.client.post(`ctrl/Companies/save-settings`, settings);
  }

  public update(model: ICompanyUpdateModel) {
    return this.client.put("/ctrl/Companies", model);
  }

  public delete(id: number): Observable<any> {
    return this.client.delete(`/ctrl/Companies/${id}`);
  }
}
